import React, { useEffect, useState } from "react";
import { UserPlus, UserMinus, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";

export const TeamPanel = () => {
  const { user } = useAuth();
  const [team, setTeam] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", password: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/team").then((r) => setTeam(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/team", form);
      toast.success(data.promoted ? `${data.email} already had an account — now an admin.` : `${data.email} added. Share their temporary password.`);
      setForm({ email: "", name: "", password: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't add team member.");
    } finally { setBusy(false); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove admin access for ${m.email}? They keep a normal account.`)) return;
    try {
      await api.delete(`/admin/team/${m.user_id}`);
      toast.success("Admin access removed.");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't remove."); }
  };

  const input = "w-full bg-white border border-[color:var(--line)] rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[color:var(--ocean)]";
  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-8" data-testid="team-panel">
      <form onSubmit={invite} className="lg:col-span-2 bg-white rounded-2xl border border-[color:var(--line)] p-6 space-y-4 self-start">
        <div className="font-display text-2xl inline-flex items-center gap-2"><UserPlus size={18} /> Add a team member</div>
        <p className="text-xs text-[color:var(--ink-2)]">If they already have a Hey GC account they'll simply be promoted. Otherwise set a temporary password and share it with them — they can change it from the Password button.</p>
        <input data-testid="team-email" type="email" required placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} />
        <input data-testid="team-name" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} />
        <input data-testid="team-password" placeholder="Temporary password (new accounts, min 8)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={input} />
        <button disabled={busy} data-testid="team-invite-btn" className="btn-pill btn-primary w-full justify-center disabled:opacity-50">{busy ? "Adding…" : "Grant admin access"}</button>
      </form>
      <div className="lg:col-span-3 rounded-2xl border border-[color:var(--line)] bg-white overflow-hidden">
        <div className="px-5 py-3 border-b border-[color:var(--line)] text-[0.68rem] uppercase tracking-[0.14em] font-bold text-[color:var(--ink-2)]">Admins ({team.length})</div>
        {team.map((m) => (
          <div key={m.user_id} data-testid={`team-row-${m.user_id}`} className="flex items-center justify-between gap-3 px-5 py-3 border-b border-[color:var(--line)] last:border-0">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-full bg-[color:var(--sand)] grid place-items-center text-[color:var(--ocean)] flex-shrink-0"><ShieldCheck size={16} /></div>
              <div className="min-w-0">
                <div className="font-semibold text-sm text-[color:var(--ocean)] truncate">{m.name} {m.user_id === user.user_id && <span className="text-xs font-normal text-[color:var(--ink-2)]">(you)</span>}</div>
                <div className="text-xs text-[color:var(--ink-2)] truncate">{m.email} · {m.auth_provider}</div>
              </div>
            </div>
            {m.user_id !== user.user_id && (
              <button onClick={() => remove(m)} data-testid={`team-remove-${m.user_id}`} title="Remove admin access" className="p-2 rounded-full hover:bg-red-50 text-red-600 flex-shrink-0"><UserMinus size={15} /></button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
